import toast from '@/services/toasts/index.js'

/**
 * A generic debounced batch queue that collects items and processes them
 * in parallel after a configurable debounce period.
 *
 * @example
 * const queue = new DebouncedBatchQueue({
 *   debounceMs: 500,
 *   processor: (item) => api.process(item),
 *   toasts: {
 *     success: (count) => `${count} items processed`,
 *     failure: (count) => `${count} items failed`,
 *   },
 *   onComplete: ({ successCount, failureCount }) => {
 *     commit('SET_BATCH_FINALIZED')
 *   }
 * })
 *
 * queue.add({ id: 1, data: 'foo' })
 * queue.add({ id: 2, data: 'bar' })
 */
export class DebouncedBatchQueue {
  #queue = []
  #pendingIds = new Set()
  #timeoutId = null
  #debounceTime
  #processor
  #isSuccess
  #toasts
  #onComplete
  #idKey

  /**
   * @param {number} [debounceTime] - Milliseconds to wait before flushing
   * @param {Function} processor - Async function to process each item, receives (item) => Promise<result>
   * @param {Function} [isSuccess] - Predicate to determine success, defaults to (result) => result?.isSuccess
   * @param {Object} [toasts] - Optional toast configuration
   * @param {Function} [toasts.success] - Function returning success message, receives (count) => string
   * @param {Function} [toasts.failure] - Function returning failure message, receives (count) => string
   * @param {Function} [onComplete] - Callback after batch completes, receives ({ successCount, failureCount, results })
   * @param {string} [idKey] - Property name for tracking pending items (e.g., 'bookingId')
   */
  constructor({
    debounceTime = 500,
    processor,
    isSuccess = (result) => result?.isSuccess,
    toasts = null,
    onComplete = null,
    idKey = null,
  }) {
    if (typeof processor !== 'function') {
      throw new Error('DebouncedBatchQueue: processor function is required')
    }

    this.#debounceTime = debounceTime
    this.#processor = processor
    this.#isSuccess = isSuccess
    this.#toasts = toasts
    this.#onComplete = onComplete
    this.#idKey = idKey
  }

  /**
   * Add an item to the queue. Resets the debounce timer.
   * @param {*} item - The item to queue
   */
  add(item) {
    this.#queue.push(item)

    if (this.#idKey && item[this.#idKey] !== undefined) {
      this.#pendingIds.add(item[this.#idKey])
    }

    this.#resetTimer()
  }

  /**
   * Check if an item with the given id is pending in the queue.
   * Requires idKey to be set in constructor.
   * @param {*} id - The id to check
   * @returns {boolean}
   */
  isPending(id) {
    return this.#pendingIds.has(id)
  }

  /**
   * Get current queue length.
   * @returns {number}
   */
  get length() {
    return this.#queue.length
  }

  /**
   * Clear the queue and cancel any pending flush.
   */
  clear() {
    this.clearTimer()
    this.#queue = []
    this.#pendingIds.clear()
  }

  #resetTimer() {
    this.clearTimer()
    this.#timeoutId = setTimeout(() => this.flush(), this.#debounceTime)
  }

  clearTimer() {
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId)
      this.#timeoutId = null
    }
  }

  async flush() {
    const items = [...this.#queue]
    this.#queue = []
    this.#pendingIds.clear()
    this.#timeoutId = null

    if (items.length === 0) return

    const results = await Promise.all(items.map(this.#processor))

    const successCount = results.filter(this.#isSuccess).length
    const failureCount = results.length - successCount

    this.showToasts(successCount, failureCount)

    if (this.#onComplete) {
      this.#onComplete({ successCount, failureCount, results })
    }
  }

  showToasts(successCount, failureCount) {
    if (!this.#toasts) return

    if (successCount > 0 && this.#toasts.success) {
      toast.success(this.#toasts.success(successCount))
    }

    if (failureCount > 0 && this.#toasts.failure) {
      toast.error(this.#toasts.failure(failureCount))
    }
  }
}
